"use client";

import {
  type MouseEvent,
  type ReactElement,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { motion } from "motion/react";
import { isString, mergeClasses } from "@huub-web/utils";
import {
  Card,
  Flex,
  IconButton,
  type IconName,
  Indicator,
  type IndicatorProps,
  Text,
  Tooltip,
} from "@huub-web/ui";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { HotspotRecord } from "../MapboxMapView";

import {
  closeStyle,
  hotspotCardStyle,
  hotspotStyle,
  hotspotTitleStyle,
} from "./Hotspot.css";

export type HotspotProps = RecipeVariants<typeof hotspotStyle> & {
  left?: number;
  top?: number;
  title?: string;
  subtitle?: ReactNode;
  indicatorVariant?: IndicatorProps["variant"];
  delay?: number;
  animate?: boolean;
  type?: HotspotRecord["type"];
  tooltipHideDelay?: number;
  icon?: IconName | ReactElement<any>;
  tooltip?: ReactNode;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  closable?: boolean;
  /** If true, forces the tooltip to be visible (overrides user close) */
  active?: boolean;
};

export const Hotspot: FC<HotspotProps> = ({
  left,
  top,
  title,
  subtitle,
  indicatorVariant = "secondary",
  delay = 0,
  animate = true,
  size = "s",
  type = "marker",
  tooltip,
  tooltipHideDelay,
  icon = "homeFilled",
  onClick,
  closable,
  active,
}) => {
  const [showTooltip, setShowTooltip] = useState(true);

  useEffect(() => {
    if (!tooltipHideDelay) {
      return;
    }

    setTimeout(() => {
      setShowTooltip(false);
    }, tooltipHideDelay * 1000);
  }, [tooltipHideDelay]);

  // Force tooltip visible when active (e.g., during active route)
  const isTooltipVisible = active || showTooltip;

  return (
    <Tooltip value={isTooltipVisible ? tooltip : ""} offset="s">
      <motion.div
        onClick={onClick}
        style={{
          left: left ? `${left}%` : undefined,
          top: top ? `${top}%` : undefined,
          position: "absolute",
        }}
        initial={
          animate ? { opacity: 0, scale: 0.4 } : { opacity: 1, scale: 1 }
        }
        animate={
          animate ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.4 }
        }
        transition={{ duration: 0.3, delay }}
      >
        <div
          className={mergeClasses(
            hotspotStyle({ size, type, hoverable: !!onClick }),
          )}
        >
          {isTooltipVisible && (title || subtitle) && (
            <Card
              className={mergeClasses(hotspotCardStyle({ closable }))}
              elevated
              edgeDirection="horizontal"
              padding="s"
            >
              <Indicator
                icon={icon || "homeFilled"}
                type="square"
                variant={indicatorVariant}
              />
              <Flex direction="column" gap="xxs2" grow>
                <Text variant="bodyM" bold className={hotspotTitleStyle}>
                  {title}
                </Text>
                {isString(subtitle) ? (
                  <Text variant="bodyS" color="currentColor50">
                    {subtitle}
                  </Text>
                ) : (
                  subtitle
                )}
              </Flex>
              {closable && (
                <IconButton
                  variant="opaque"
                  icon="close"
                  size="xxs"
                  onClick={() => setShowTooltip(false)}
                  className={closeStyle()}
                  border="default"
                  rounded
                />
              )}
            </Card>
          )}
        </div>
      </motion.div>
    </Tooltip>
  );
};
